import { useCallback } from 'react';
import { PostDocumentUploadResponse } from '@iwoca/lapi-client/edge';
import { FileDrop } from '@iwoca/orion';
import { useAmplitude } from 'hooks/useAmplitude';
import { fetchSaveUploadedDocuments } from 'lendingApi/lendingApi';
import { TUploadRejectReasonCode } from 'types';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import { v4 } from 'uuid';

type TProps = {
  stateKey: string;
  documentType?: PostDocumentUploadResponse['data']['document_type'] | null;
  isOptional?: boolean;
  setFileLoading: (id: string, name: string) => void;
  setFileSuccess: (id: string) => void;
  setFileError: (id: string, errorReason: string) => void;
  setFileReject: (id: string, rejectReason: TUploadRejectReasonCode) => void;
};

export const UploadDropButton = ({
  stateKey,
  documentType,
  setFileLoading,
  isOptional,
  setFileSuccess,
  setFileError,
  setFileReject,
}: TProps) => {
  const documentUploadType = documentType ? documentType : 'other';
  const { trackEvent } = useAmplitude();

  const onChange = useCallback(
    async (acceptedFiles: any) => {
      await acceptedFiles.forEach(async (file: File) => {
        const uuid = v4();
        setFileLoading(uuid, file.name);
        const body = new FormData();

        body.append('document', file);
        body.append('document_type', documentUploadType);

        const response = await fetchSaveUploadedDocuments({
          stateKey,
          body,
        });

        if (response?.errors?.[0]) {
          setFileError(uuid, response.errors[0].detail);
        } else if (response?.attributes?.status === 'rejected') {
          setFileReject(uuid, response?.attributes?.reject_reason);
        } else {
          setFileSuccess(uuid);
          trackEvent(AMPLITUDE_EVENTS.documentUploaded, {
            'Document type': documentType || 'none',
            Required: !isOptional,
          });
        }
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [documentType, stateKey],
  );

  return (
    <FileDrop
      accept=".jpg, .jpeg, .png, .pdf"
      data-testid="upload-drop"
      onChange={onChange}
      stacked={false}
    />
  );
};
